import type { AdminExpertClaim, ExpertClaimType } from '@shopexperts/legacy-api';

/** Server limit for `admin_note` on the note, approve, and reject endpoints. */
export const CLAIM_NOTE_MAX_LENGTH = 2000;

/**
 * Single source of the note-length guard. ClaimNoteField uses it for its own
 * error display; the approve/reject dialogs and the drawer use it to block
 * submission — keeping the rule here means they can never drift apart.
 */
export function isClaimNoteOverLimit(note: string): boolean {
  return note.length > CLAIM_NOTE_MAX_LENGTH;
}

/**
 * The card accent strip encodes actionability, not raw status:
 * - amber  — undecided, waiting on the claimant's email verification
 * - sky    — undecided and verified: waiting on an admin (the only state
 *            that needs you now, so it gets the only hue the admin area
 *            doesn't use elsewhere)
 * - emerald / rose — decided
 */
export type ClaimLifecycle = 'awaiting-verification' | 'ready-for-review' | 'approved' | 'rejected';

export function getClaimLifecycle(claim: Pick<AdminExpertClaim, 'status' | 'decision'>): ClaimLifecycle {
  if (claim.decision === 'approved') return 'approved';
  if (claim.decision === 'rejected') return 'rejected';
  return claim.status === 'verified' ? 'ready-for-review' : 'awaiting-verification';
}

export const lifecycleStripClasses: Record<ClaimLifecycle, string> = {
  'awaiting-verification': 'bg-amber-400',
  'ready-for-review': 'bg-sky-400',
  approved: 'bg-emerald-400',
  rejected: 'bg-rose-400',
};

/**
 * Rendered through the shared UiChip: `variant` picks the chip frame
 * (`outline` adds the border for the undecided state), while bg/text
 * override the palette per state.
 */
export interface ClaimChipAppearance {
  label: string;
  variant: 'plain' | 'outline';
  bgClass: string;
  textClass: string;
}

export const statusChip: Record<AdminExpertClaim['status'], ClaimChipAppearance> = {
  pending: { label: 'Pending', variant: 'plain', bgClass: 'bg-amber-50', textClass: 'text-amber-700' },
  verified: { label: 'Verified', variant: 'plain', bgClass: 'bg-emerald-50', textClass: 'text-emerald-700' },
};

export interface DecisionChip extends ClaimChipAppearance {
  /** True for system auto-rejections (`decision === 'rejected' && decided_by === null`). */
  isAutoRejected: boolean;
}

export function getDecisionChip(claim: Pick<AdminExpertClaim, 'decision' | 'decided_by'>): DecisionChip {
  if (claim.decision === 'approved') {
    return { label: 'Approved', variant: 'plain', bgClass: 'bg-emerald-50', textClass: 'text-emerald-700', isAutoRejected: false };
  }
  if (claim.decision === 'rejected') {
    const isAutoRejected = claim.decided_by === null;
    return {
      label: isAutoRejected ? 'Auto-rejected' : 'Rejected',
      variant: 'plain',
      bgClass: 'bg-rose-50',
      textClass: 'text-rose-600',
      isAutoRejected,
    };
  }
  return { label: 'Undecided', variant: 'outline', bgClass: 'bg-white', textClass: 'text-gray-600', isAutoRejected: false };
}

/** Mirrors the Expert Manager type colors so admins learn one vocabulary. */
export const claimTypeChip: Record<ExpertClaimType, ClaimChipAppearance> = {
  freelancer: { label: 'Freelancer', variant: 'plain', bgClass: 'bg-violet-50', textClass: 'text-violet-600' },
  agency: { label: 'Agency', variant: 'plain', bgClass: 'bg-blue-50', textClass: 'text-blue-600' },
};

/**
 * Filter options shared by ExpertClaimFilters (the aside panel) and
 * AdminExpertClaimsPage (the active-filter tag row), so both surfaces render
 * the same labels and dot colors from the page's own v-model state.
 * `none` / `any` are the API's own decision-filter values; labels translate
 * them into what an admin is actually looking for.
 */
export interface ClaimFilterOption {
  value: string;
  label: string;
  dot?: string;
}

export const claimDecisionFilterOptions: ClaimFilterOption[] = [
  { value: 'none', label: 'Undecided', dot: 'bg-sky-400' },
  { value: 'approved', label: 'Approved', dot: 'bg-emerald-400' },
  { value: 'rejected', label: 'Rejected', dot: 'bg-rose-400' },
  { value: 'any', label: 'Any decided', dot: 'bg-gray-400' },
];

export const claimStatusFilterOptions: ClaimFilterOption[] = [
  { value: 'pending', label: 'Pending', dot: 'bg-amber-400' },
  { value: 'verified', label: 'Verified', dot: 'bg-emerald-400' },
];

export const claimTypeFilterOptions: ClaimFilterOption[] = [
  { value: 'freelancer', label: 'Freelancer' },
  { value: 'agency', label: 'Agency' },
];
